import { useEffect, useRef, useState } from "react";
import { Link } from "react-router";
import { ArrowRight, Link2, X } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { env } from "@/lib/env";
import {
  clearPendingConnectInvite,
  getPendingConnectInvite,
  type ConnectInviteKind,
} from "@/lib/pending-connect-invite";
import { AceiteMultiClinicaDialog } from "@/components/partners/AceiteMultiClinicaDialog";
import { ConectarPeloCatalogoDialog } from "@/components/prosthesis/catalogo-publico/ConectarPeloCatalogoDialog";

const REQUIRED_TYPE: Record<Exclude<ConnectInviteKind, "catalogo">, "DENTAL_OFFICE" | "DENTAL_LAB"> = {
  partner: "DENTAL_OFFICE",
  lab: "DENTAL_LAB",
};
const ACCEPT_PATH: Record<Exclude<ConnectInviteKind, "catalogo">, string> = {
  partner: "/conectar-dentista",
  lab: "/conectar-laboratorio",
};

/**
 * Fecha o loop do convite de conexão entre organizações quando quem abriu o link já tinha conta.
 *
 * Kind "partner": abre a escolha de clínicas (AceiteMultiClinicaDialog) DENTRO do app — não
 * depende mais da organização ativa: quem tem várias clínicas conecta todas ou escolhe, e a já
 * conectada aparece como "Parceria já existe". Kind "lab" segue no redirecionamento pra página
 * pública, com o aviso persistente quando o tipo da organização ativa não bate.
 */
export function PendingConnectInviteBanner() {
  const [pending, setPending] = useState(() => getPendingConnectInvite());
  const meQuery = trpc.me.current.useQuery(undefined, { enabled: !!pending });
  const redirecionado = useRef(false);

  const limpar = () => {
    clearPendingConnectInvite();
    setPending(null);
  };

  const activeTypes = meQuery.data?.activeOrganization?.types ?? [];
  const labBate = pending?.kind === "lab" && activeTypes.includes(REQUIRED_TYPE.lab);

  useEffect(() => {
    if (!pending || !labBate || redirecionado.current) return;
    redirecionado.current = true;
    clearPendingConnectInvite();
    window.location.href = `${env.landingUrl}${ACCEPT_PATH.lab}/${pending.token}`;
  }, [pending, labBate]);

  if (!pending) return null;

  // Parceria: a folha de escolha resolve qualquer combinação de organizações — abre direto.
  if (pending.kind === "partner") {
    return (
      <AceiteMultiClinicaDialog
        aberto
        origem={{ tipo: "token", token: pending.token }}
        // Fechar sem ter clínica nenhuma só some com o diálogo; o token continua guardado e a
        // folha reabre no próximo acesso. Descartar de verdade é decisão explícita lá dentro.
        onFechar={(descartar) => (descartar ? limpar() : setPending(null))}
        onSucesso={limpar}
      />
    );
  }

  // Veio do link público do catálogo de um laboratório: conectar ou, já conectado, pedir.
  if (pending.kind === "catalogo") {
    return (
      <ConectarPeloCatalogoDialog
        codigo={pending.token}
        linha={pending.linha}
        // Sem descartar (ex.: foi trocar de organização), o pendente volta no próximo acesso.
        onFechar={(descartar) => (descartar ? limpar() : setPending(null))}
      />
    );
  }

  if (labBate) return null;

  return (
    <div className="flex flex-wrap items-center justify-between gap-2 border-b border-brand-200 bg-brand-50 px-4 py-2 text-[13px] text-brand-800 dark:border-primary/30 dark:bg-primary/10 dark:text-primary">
      <div className="flex items-center gap-2">
        <Link2 className="h-4 w-4 shrink-0" />
        <span>
          Você tem uma conexão de laboratório pendente. Troque pra uma organização de laboratório
          (ou crie uma) pra concluir.
        </span>
      </div>
      <div className="flex shrink-0 items-center gap-3">
        <Link to="/organizacoes" className="inline-flex items-center gap-1 font-semibold hover:underline">
          Ver organizações
          <ArrowRight className="h-3.5 w-3.5" />
        </Link>
        <button
          type="button"
          onClick={limpar}
          title="Dispensar"
          className="text-brand-400 transition-colors hover:text-brand-600 dark:text-primary/60 dark:hover:text-primary"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
